import { useEffect, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { Loader2, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'

import type { EInvoiceTemplate } from '@/lib/api/einvoice-api'
import type { InvoiceSettingsFormData } from '@/lib/schemas/invoice-settings-schemas'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Field } from '@/components/ui/field'
import {
    InvoiceTemplateLoadStatus,
    InvoiceTemplateOption,
    InvoiceTemplateSummary,
} from '@/components/invoices/invoice-template-details'
import {
    Select,
    SelectContent,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useAuth } from '@/hooks/use-auth'
import { useEInvoiceCredential } from '@/hooks/use-einvoice-credential'
import { useSyncEInvoiceTemplates } from '@/hooks/use-einvoice-draft-mutations'
import { formatApiError } from '@/lib/format-api-error'
import { m } from '@/lib/paraglide/messages'

interface InvoiceTemplateSettingsProps {
    savedTemplateID?: string
    savedSeries?: string
}

export function InvoiceTemplateSettings({
    savedTemplateID,
    savedSeries,
}: InvoiceTemplateSettingsProps) {
    const { control, getValues, setValue } =
        useFormContext<InvoiceSettingsFormData>()
    const { hasPermission, isImpersonating } = useAuth()
    const credential = useEInvoiceCredential()
    const [templates, setTemplates] = useState<EInvoiceTemplate[]>([])
    const [hasLoaded, setHasLoaded] = useState(false)
    const sync = useSyncEInvoiceTemplates()
    const canSync =
        hasPermission('invoices.export') &&
        !isImpersonating &&
        !credential.isError &&
        Boolean(credential.data?.configured && !credential.data.invalid_since)
    const watchedID = useWatch({ control, name: 'misa_ip_template_id' })
    const watchedSeries = useWatch({ control, name: 'misa_inv_series' })
    const selectedID =
        getValues('misa_ip_template_id') || watchedID || savedTemplateID || ''
    const selectedSeries =
        getValues('misa_inv_series') || watchedSeries || savedSeries || ''
    const activeSelection = templates.find(
        (template) => template.ip_template_id === selectedID,
    )
    const savedFallback: EInvoiceTemplate | undefined = selectedID
        ? {
              ip_template_id: selectedID,
              inv_template_no: '',
              inv_series: selectedSeries,
              template_name: '',
              is_more_vat_rate: false,
              invoice_with_code: false,
          }
        : undefined
    const selectedTemplate = activeSelection ?? savedFallback
    const options =
        savedFallback && !activeSelection
            ? [...templates, savedFallback]
            : templates
    const isStale = hasLoaded && Boolean(savedFallback && !activeSelection)

    const applyTemplates = (data: EInvoiceTemplate[]) => {
        setTemplates(data)
        setHasLoaded(true)
    }

    useEffect(() => {
        if (!canSync) return
        sync.mutate(undefined, { onSuccess: applyTemplates })
    }, [canSync])

    const handleSync = () => {
        sync.mutate(undefined, {
            onSuccess: (data) => {
                applyTemplates(data)
                toast.success(m.einvoice_templates_synced())
            },
        })
    }

    const error = sync.isError
        ? formatApiError(sync.error, m.einvoice_templates_sync_failed())
        : undefined

    return (
        <Card>
            <CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1">
                    <CardTitle>{m.invoice_misa_template_title()}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                        {m.invoice_misa_template_help()}
                    </p>
                </div>
                {canSync ? (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={handleSync}
                        disabled={sync.isPending}
                        className="w-full sm:w-auto"
                    >
                        {sync.isPending ? (
                            <Loader2
                                aria-hidden
                                className="size-4 animate-spin"
                            />
                        ) : (
                            <RefreshCw aria-hidden className="size-4" />
                        )}
                        {m.invoice_misa_template_sync()}
                    </Button>
                ) : null}
            </CardHeader>
            <CardContent className="space-y-4">
                <Field
                    name="misa_ip_template_id"
                    label={m.invoice_misa_template_label()}
                >
                    <Controller
                        control={control}
                        name="misa_ip_template_id"
                        render={({ field }) => (
                            <Select
                                value={field.value}
                                onValueChange={(templateID) => {
                                    field.onChange(templateID)
                                    const template = options.find(
                                        (item) =>
                                            item.ip_template_id === templateID,
                                    )
                                    setValue(
                                        'misa_inv_series',
                                        template?.inv_series ?? '',
                                        { shouldDirty: true },
                                    )
                                }}
                                disabled={
                                    sync.isPending && options.length === 0
                                }
                            >
                                <SelectTrigger
                                    id="misa_ip_template_id"
                                    className="h-auto min-h-11 md:min-h-9"
                                >
                                    <SelectValue
                                        placeholder={m.invoice_misa_template_placeholder()}
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    {options.map((template) => (
                                        <InvoiceTemplateOption
                                            key={`${template.ip_template_id}-${template.inv_series}`}
                                            template={template}
                                        />
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    />
                </Field>
                {selectedTemplate ? (
                    <InvoiceTemplateSummary
                        template={activeSelection}
                        series={selectedSeries}
                    />
                ) : null}
                <InvoiceTemplateLoadStatus
                    isPending={sync.isPending}
                    hasLoaded={hasLoaded}
                    count={templates.length}
                    error={error}
                    isStale={isStale}
                />
            </CardContent>
        </Card>
    )
}
